import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import { Typography } from "@mui/material";
import { onboardingStatusSteps } from "../../userOnboardingConstants";
import postPOImage from "../../assets/images/userOnboarding/Agent_Post_PO_Pose.png";
import kavidaLogo from "../../assets/images/userOnboarding/kavidaLogo.png";
import tickCircleGreen from "../../assets/images/userOnboarding/tick-circle-green.svg";
import infoCircle from "../../assets/images/userOnboarding/info-circle-yellow.svg";
import arrowRightPurple from "../../assets/images/userOnboarding/arrow-right.svg";
import arrowRightGrey from "../../assets/images/userOnboarding/arrow-right-grey.svg";
import { useNavigate } from "react-router-dom";
import {
  getAuthUserOnboarding,
  setAuthUserOnboarding,
} from "../../utils/session";

// TODO: KAVC-2249 — keycloakOnboarding removed; pending onboarding team auth decision
const keycloakOnboarding = null;

const OnboardingContainer = styled('div')(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  minHeight: "100vh",
  backgroundColor: "#F7F7F7",
}));

const OnboardingLeft = styled('div')(({ theme }) => ({
  flex: 2,
  padding: "80px 108px 0 104px",
}));

const OnboardingRight = styled('div')(({ theme }) => ({
  flex: 3,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  backgroundColor: "#E8E8E8",
}));

const Title = styled(Typography)(({ theme }) => ({
  color: "#171717",
  marginBottom: "24px",
  marginTop: "88px",
  fontFamily: "Figtree",
  fontSize: "32px",
  fontWeight: 600,
}));

const TextStyles = styled(Typography)(({ theme }) => ({
  color: "#747474",
  fontFamily: "Figtree",
  fontSize: "16px",
  fontWeight: 400,
  marginBottom: "32px",
}));

const StepContainer = styled('div')(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginBottom: "40px",
}));

const Step = styled('div')(({ theme }) => ({
  display: "flex",
  gap: "4px",
  alignItems: "center",
}));

const StepText = styled(Typography)(({ theme }) => ({
  fontFamily: "Figtree",
  fontSize: "16px",
  fontWeight: 600,
}));

const TryAgain = styled('div')(({ theme }) => ({
  display: "flex",
  gap: "4px",
  color: "#6933FF",
  alignItems: "center",
  marginLeft: "8px",
}));

const ResendEmailContainer = styled('div')(({ theme }) => ({
  marginTop: "40px",
}));

const ResendEmailText = styled('div')(({ theme }) => ({
  display: "flex",
  gap: "4px",
  alignItems: "center",
  color: "#D1D1D1",
}));

const ResendEmailEnabled = styled('div')(({ theme }) => ({
  display: "flex",
  gap: "4px",
  alignItems: "center",
  color: "#6933FF",
  cursor: "pointer",
}));

const OnboardingStatus = ({ isCompletedOnboarding }) => {
  const [count, setCount] = useState(0);
  const [isResendEmail, setIsResendEmail] = useState(false);
  const navigate = useNavigate();
  const authUser = getAuthUserOnboarding();

  const isLastStep = (index) => index === onboardingStatusSteps.length - 1;

  useEffect(() => {
    const initializeKeycloak = async () => {
      try {
        if (!keycloakOnboarding) return; // TODO: KAVC-2249 pending auth decision
        const authenticated = await keycloakOnboarding.init({
          onLoad: "login-required",
          checkLoginIframe: false,
        });

        if (authenticated) {
          const user = await keycloakOnboarding.loadUserInfo();
          setAuthUserOnboarding(user);
                  navigate("/onboarding/completed");
      } else {
        navigate("/onboarding/retry");
        }
      } catch (error) {
        console.log(error);
      }
    };
    if (isCompletedOnboarding) {
      initializeKeycloak();
    }
  }, []);

  useEffect(() => {
    if (count > 0) {
      const timer = setInterval(
        () => setCount((prevCount) => prevCount - 1),
        1000
      );
      return () => clearInterval(timer);
    } else {
      setIsResendEmail(false);
    }
  }, [count]);

  const handleResendEmail = () => {
    setIsResendEmail(true);
    setCount(60);
  };

  const renderStepIcon = (index) => {
    const isFailedLastStep = !isCompletedOnboarding && isLastStep(index);
    return (
      <img
        src={isFailedLastStep ? infoCircle : tickCircleGreen}
        alt={isFailedLastStep ? "info-icon" : "tick-icon"}
      />
    );
  };

  const renderSteps = () =>
    onboardingStatusSteps.map((step, index) => {
      const isFailedLastStep = !isCompletedOnboarding && isLastStep(index);
      const stepColor = isFailedLastStep ? "#FEB101" : "#2F9801"; // Yellow for failed last step, green otherwise

      return (
        <Step key={index}>
          {renderStepIcon(index)}
          <StepText
            variant="body1"
            style={{ color: stepColor }}
          >
            {step}
          </StepText>
          {isFailedLastStep && (
            <TryAgain>
              <span>Try again</span>
              <img src={arrowRightPurple} alt="arrow-right" />
            </TryAgain>
          )}
        </Step>
      );
    });

  const titleText = isCompletedOnboarding
    ? "Your personalized AI agent is almost ready!"
    : "Almost there...";

  const bodyText = isCompletedOnboarding
    ? "Congratulations! You've successfully completed all the steps to personalize your AI Agent. An email with a link to start using Agent PO is on its way to your inbox."
    : "One step left to complete before meeting your personalized AI agent on Kavida.ai";

  return (
    (authUser || !isCompletedOnboarding) && (
      <OnboardingContainer>
        <OnboardingLeft>
          <img src={kavidaLogo} alt="Kavida Logo" width={117} height={24} />
          <Title variant="h1">
            {titleText}
          </Title>
          <TextStyles variant="body1">
            {bodyText}
          </TextStyles>
          <StepContainer>{renderSteps()}</StepContainer>
          {!isCompletedOnboarding && (
            <>
              <TextStyles variant="body1">
                We've sent you an email with instructions to complete your
                single sign-on process. If you haven't received it, please click
                the button below, and we'll resend the email.
              </TextStyles>
              <ResendEmailContainer>
                {isResendEmail ? (
                  <ResendEmailText>
                    <span>Resend Email</span>
                    <img src={arrowRightGrey} alt="arrow-right" />
                  </ResendEmailText>
                ) : (
                  <ResendEmailEnabled
                    onClick={handleResendEmail}
                  >
                    <span>Resend Email</span>
                    <img src={arrowRightPurple} alt="arrow-right" />
                  </ResendEmailEnabled>
                )}
                {isResendEmail && (
                  <TextStyles variant="body1">
                    We've resent the email. Try again in {count} seconds...
                  </TextStyles>
                )}
              </ResendEmailContainer>
            </>
          )}
        </OnboardingLeft>
        <OnboardingRight>
          <img src={postPOImage} alt="AI Agent" />
        </OnboardingRight>
      </OnboardingContainer>
    )
  );
};

export default OnboardingStatus;
